import { useQuery } from '@tanstack/react-query';

import { useState } from 'react';

import { Close, Save } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, TableCell, TableRow, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { useApiClient } from '@@common';

import { AddPropertyV2Model, DataSchemaClient, NewEntityAsNewPropertyRequest, PrimitiveType, SchemaSummaryModel, SchemaTypes } from '../../../api/api';
import { getRequiredFieldProps, usePropertySaveValidation } from './usePropertySaveValidation';

interface Props {
	onSave: (property: NewEntityAsNewPropertyRequest) => Promise<void> | void;
	onSaveReference: (property: AddPropertyV2Model) => Promise<void> | void;
	onIsEditingChanged: (isEditing: boolean) => void;
	contextId: string;
	name: string;
	onNameChange: (value: string) => void;
	description: string;
	onDescriptionChange: (value: string) => void;
	isRequired: boolean;
	onIsRequiredChange: (value: boolean) => void;
	isMultiple: boolean;
	onIsMultipleChange: (value: boolean) => void;
}

const emptyNewEntityDetails = () => ({
	entityName: '',
	description: '',
	schemaType: SchemaTypes.RootDataSchema,
	properties: [],
	enumProperties: [],
	referenceProperties: [],
});

const emptyReferenceFields = () => ({
	propertyType: PrimitiveType.Ref,
	isReference: true,
	isEnum: false,
	referenceSchemaId: '',
	referenceSchemaContextId: '',
});

const getSchemaOptionKey = (schema: SchemaSummaryModel) => `${schema.contextId}:${schema.id}`;

const AddSchemaPropertyRow: React.FC<Props> = ({
	onSave,
	onSaveReference,
	onIsEditingChanged,
	contextId,
	name,
	onNameChange,
	description,
	onDescriptionChange,
	isRequired,
	onIsRequiredChange,
	isMultiple,
	onIsMultipleChange,
}) => {
	const [mode, setMode] = useState<'new' | 'existing'>('new');
	const [newEntityDetails, setNewEntityDetails] = useState(emptyNewEntityDetails());
	const [referenceFields, setReferenceFields] = useState(emptyReferenceFields());
	const [selectedSchema, setSelectedSchema] = useState<SchemaSummaryModel | null>(null);

	const isNameValid = name.trim().length > 0;
	const isTypeValid = mode === 'new' ? newEntityDetails.entityName.trim().length > 0 : !!selectedSchema;
	const canSave = isNameValid && isTypeValid;
	const { saveAttempted, validateForSave, resetValidation } = usePropertySaveValidation(canSave);

	const client = useApiClient(DataSchemaClient);
	const { data: availableSchemas = [], isLoading: loadingSchemas } = useQuery({
		queryKey: ['entities-all-schemas', contextId],
		queryFn: () => client.getEntitiesForContext(contextId, undefined, false),
		enabled: !!contextId && mode === 'existing',
		staleTime: 60000,
	});

	const reset = () => {
		setNewEntityDetails(emptyNewEntityDetails());
		setReferenceFields(emptyReferenceFields());
		setSelectedSchema(null);
		resetValidation();
		onIsEditingChanged(false);
	};

	const handleSave = async () => {
		if (!validateForSave()) {
			return;
		}

		try {
			if (mode === 'new') {
				await onSave({ propertyName: name, propertyDescription: description, isRequired, isMultiple, newEntityDetails });
			} else {
				if (!selectedSchema) return;
				await onSaveReference({
					...referenceFields,
					name,
					description,
					isRequired,
					isMultiple,
					referenceSchemaId: selectedSchema.id,
					referenceSchemaContextId: selectedSchema.contextId,
				});
			}
			reset();
		} catch (err) {
			console.error('Failed to save property', err);
		}
	};

	return (
		<>
			<TableRow>
				<TableCell>
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						{...getRequiredFieldProps(saveAttempted && !isNameValid)}
						placeholder="Property name"
						value={name}
						onChange={(e) => onNameChange(e.target.value)}
					/>
				</TableCell>
				<TableCell>
					<Box display="flex" flexDirection="column" gap={0.5}>
						<ToggleButtonGroup
							value={mode}
							exclusive
							onChange={(_, v) => {
								if (v) setMode(v);
							}}
							size="small"
						>
							<ToggleButton value="new">New schema</ToggleButton>
							<ToggleButton value="existing">Existing schema</ToggleButton>
						</ToggleButtonGroup>
						{mode === 'new' ? (
							<TextField
								variant="outlined"
								size="small"
								fullWidth
								{...getRequiredFieldProps(saveAttempted && !isTypeValid)}
								placeholder="Schema name"
								value={newEntityDetails.entityName}
								onChange={(e) => setNewEntityDetails((p) => ({ ...p, entityName: e.target.value }))}
							/>
						) : (
							<Autocomplete
								size="small"
								options={availableSchemas}
								loading={loadingSchemas}
								getOptionKey={getSchemaOptionKey}
								getOptionLabel={(o) => o.name ?? ''}
								isOptionEqualToValue={(option, value) => getSchemaOptionKey(option) === getSchemaOptionKey(value)}
								value={selectedSchema}
								onChange={(_, v) => {
									setSelectedSchema(v);
									if (v && !name) {
										onNameChange(v.name);
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										{...getRequiredFieldProps(saveAttempted && !isTypeValid)}
										placeholder="Select schema"
										variant="outlined"
									/>
								)}
							/>
						)}
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isRequired} onChange={(e) => onIsRequiredChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isMultiple} onChange={(e) => onIsMultipleChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell></TableCell>
				<TableCell>
					<Box display="flex" justifyContent="right">
						<IconButton onClick={handleSave} size="small" color="primary">
							<Save />
						</IconButton>
						<IconButton onClick={() => onIsEditingChanged(false)} size="small">
							<Close />
						</IconButton>
					</Box>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={6}>
					<TextField
						label="Description"
						variant="outlined"
						size="small"
						fullWidth
						multiline
						value={description}
						onChange={(e) => onDescriptionChange(e.target.value)}
					/>
				</TableCell>
			</TableRow>
		</>
	);
};

export default AddSchemaPropertyRow;
